import { Box, Stack } from '@mui/material';

import { SMBold } from '@/typography/Typography';
import { Dropdown } from '@/components/shared/Dropdown';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { SearchGrid } from '@/components/SearchGrid';
import {
    useEmployeeDetailsQuery,
    useReportsQuery
} from '@/services/reportsQuery';
import { SicknessDashboardCharts } from './SicknessDashboardCharts';

import { DateRangeDropdown } from '@/components/shared/DateRangeDropdown';
import { useErrorHandling } from '@/utils/errorHandling';

export const SicknessDashboard = () => {
    const dispatch = useAppDispatch();

    const [departments, setDepartments] = useState<string[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<string>();

    const { t } = useTranslation();

    const [startDate, setStartDate] = useState<Date>(
        dayjs().startOf('month').toDate()
    );
    const [endDate, setEndDate] = useState<Date>(
        dayjs().endOf('month').toDate()
    );

    const { getDepartmentsQuery } = useReportsQuery({
        enableGetDepartmentsQuery: true
    });

    const {
        data: getEmployeeDetailsData,
        refetch: getEmployeeDetailsQuery,
        isPending: getEmployeeDetailsPending,
        error: getEmployeeDetailsError
    } = useEmployeeDetailsQuery(getDepartmentsQuery.isFetched, {
        departments:
            selectedDepartment == 'All' ? undefined : selectedDepartment,
        startDate: dayjs(startDate)?.format('YYYY-MM-DD'),
        endDate: dayjs(endDate).endOf('month').format('YYYY-MM-DD')
    });

    useEffect(() => {
        if (getDepartmentsQuery.data) {
            setDepartments([
                'All',
                ...getDepartmentsQuery.data.data.plandayDepartments.map(
                    (d: any) => d.name
                )
            ]);
            if (!selectedDepartment) {
                setSelectedDepartment('All');
            }
        }
    }, [getDepartmentsQuery.data]);

    useEffect(() => {
        if (selectedDepartment && startDate && endDate) {
            getEmployeeDetailsQuery();
        }
    }, [selectedDepartment, startDate, endDate]);

    useLayoutEffect(() => {
        dispatch(
            setLoader(
                getEmployeeDetailsPending || getDepartmentsQuery.isPending
            )
        );
    }, [getEmployeeDetailsPending, getDepartmentsQuery.isPending]);

    const { backendErrorHandler } = useErrorHandling();

    useEffect(() => {
        if (getEmployeeDetailsError) {
            backendErrorHandler()(getEmployeeDetailsError);
        }
    }, [getEmployeeDetailsError]);

    useEffect(() => {
        if (getDepartmentsQuery.error) {
            backendErrorHandler()(getDepartmentsQuery.error);
        }
    }, [getDepartmentsQuery.error]);

    const [timeFilter, setTimeFilter] = useState('thisMonth');

    const currentYear = new Date().getFullYear();

    const filterOptions = [
        { value: 'thisMonth', name: t('currentMonth') },
        { value: 'lastMonth', name: t('lastMonth') },
        { value: 'customMonth', name: t('customMonth') },
        { value: 'currentYear', name: t('currentYear', { year: currentYear }) },
        { value: 'lastYear', name: t('lastYear', { year: currentYear - 1 }) }
    ];

    const currentDate = new Date();

    const filtereDateData = async (options: any) => {
        if (options === 'thisMonth') {
            const firstDayOfMonth = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                1
            );
            const lastDayOfMonth = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth() + 1,
                0
            );
            setStartDate(firstDayOfMonth);
            setEndDate(lastDayOfMonth);
        } else if (options === 'lastMonth') {
            const lastMonth = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth() - 1,
                1
            );
            const lastDayOfLastMonth = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                0
            );
            setStartDate(lastMonth);
            setEndDate(lastDayOfLastMonth);
        } else if (options === 'currentYear') {
            const firstDayOfYear = new Date(currentDate.getFullYear(), 0, 1);
            const lastDayOfYear = new Date(currentDate.getFullYear(), 11, 31);
            setStartDate(firstDayOfYear);
            setEndDate(lastDayOfYear);
        } else if (options === 'lastYear') {
            const firstDayOfLastYear = new Date(
                currentDate.getFullYear() - 1,
                0,
                1
            );
            const lastDayOfLastYear = new Date(
                currentDate.getFullYear() - 1,
                11,
                31
            );
            setStartDate(firstDayOfLastYear);
            setEndDate(lastDayOfLastYear);
        }
    };

    const onMonthChange = (date: Date) => {
        setTimeFilter('customMonth');

        const startOfMonth = dayjs(date).startOf('month').toDate();
        const endOfMonth = dayjs(date).endOf('month').toDate();
        setStartDate(startOfMonth);
        setEndDate(endOfMonth);
    };

    return (
        <Box>
            <SearchGrid container justifyContent={'space-between'}>
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                    <div>
                        <SMBold noWrap={true}>{t('filter_by')}:</SMBold>
                    </div>
                    <div>
                        <DateRangeDropdown
                            startDate={startDate}
                            endDate={endDate}
                            timeFilter={timeFilter}
                            setTimeFilter={setTimeFilter}
                            filterOptions={filterOptions}
                            filtereDateData={filtereDateData}
                            onMonthChange={onMonthChange}
                            showDatePicker={false}
                        />
                    </div>
                    <div style={{ width: '220px' }}>
                        <Dropdown
                            label={''}
                            choices={departments.map((d, index) => {
                                return { label: d, value: d };
                            })}
                            value={selectedDepartment}
                            onChange={function (value: any | any[]): void {
                                setSelectedDepartment(value);
                            }}
                        ></Dropdown>
                    </div>
                </Stack>
            </SearchGrid>
            <SicknessDashboardCharts
                startDate={startDate}
                endDate={endDate}
                timeFilter={timeFilter}
                getEmployeeDetailsData={getEmployeeDetailsData?.data}
            />
        </Box>
    );
};
